// Field formatters and validators
export const formatters = {
  phone: (value: string): string => {
    // Remove spaces and keep only digits and +
    let cleaned = value.replace(/[^\d+]/g, '');

    // If it starts with 0 (French format), convert to +33
    if (cleaned.startsWith('0')) {
      cleaned = '+33' + cleaned.substring(1);
    }

    // If it starts with +33, ensure proper formatting
    if (cleaned.startsWith('+33')) {
      const digits = cleaned.substring(3);

      // Format progressively: +33 6 00 00 00 00
      if (digits.length === 0) {
        return '+33 ';
      }

      let formatted = '';
      // First digit alone
      formatted += digits[0];

      // Then pairs of digits
      for (let i = 1; i < digits.length; i += 2) {
        formatted += ' ' + digits[i];
        if (i + 1 < digits.length) {
          formatted += digits[i + 1];
        }
      }

      return '+33 ' + formatted;
    }

    // If it starts with +, keep it and format
    if (cleaned.startsWith('+')) {
      const digits = cleaned.substring(1);
      return '+' + digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    }

    // Default: just format the digits
    return cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  },

  email: (value: string): string => {
    // Remove extra spaces and convert to lowercase
    return value.trim().toLowerCase();
  },

  name: (value: string): string => {
    // Ne pas trim pendant la saisie pour permettre les espaces en fin
    // Remplacer les espaces multiples par un seul espace
    const cleaned = value.replace(/\s{2,}/g, ' ');
    
    // Capitaliser chaque mot (séparés par des espaces)
    // Gérer les espaces en début et fin correctement
    const words = cleaned.split(' ');
    return words
      .map(word => {
        if (word.length === 0) return word; // Préserver les espaces vides
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  },

  companyName: (value: string): string => {
    // Remove extra spaces but keep original case
    return value.trim().replace(/\s+/g, ' ');
  },

  postalCode: (value: string): string => {
    // Format French postal code (5 digits)
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 5);
  },
};

export const validators = {
  phone: (value: string): boolean => {
    // Remove spaces but keep +
    const cleaned = value.replace(/\s/g, '');

    // International format with +33 (should have +33 followed by 9 digits)
    if (cleaned.startsWith('+33')) {
      const digits = cleaned.substring(3).replace(/\D/g, '');
      return digits.length === 9;
    }

    // Other international formats (+ followed by 10-15 digits)
    if (cleaned.startsWith('+')) {
      const digits = cleaned.substring(1).replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15;
    }

    // French format starting with 0 (will be converted by formatter)
    const digits = cleaned.replace(/\D/g, '');
    return digits.length === 10 && digits.startsWith('0');
  },

  email: (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },

  name: (value: string): boolean => {
    // At least 2 characters, only letters, spaces, hyphens, and apostrophes
    // Allow accented characters, spaces, apostrophes, and hyphens
    const nameRegex = /^[a-zA-ZÀ-ÿ\s'\-]{2,}$/;
    return nameRegex.test(value.trim());
  },

  companyName: (value: string): boolean => {
    // At least 2 characters, allow letters, numbers, spaces, and common punctuation
    return value.trim().length >= 2;
  },

  postalCode: (value: string): boolean => {
    // French postal code format (5 digits)
    const digits = value.replace(/\D/g, '');
    return digits.length === 5;
  },
};

export const getFieldFormatter = (fieldType: string) => {
  switch (fieldType) {
    case 'phone':
    case 'tel':
      return formatters.phone;
    case 'email':
      return formatters.email;
    case 'firstname':
    case 'lastname':
      return formatters.name;
    case 'companyName':
      return formatters.companyName;
    case 'postalCode':
      return formatters.postalCode;
    default:
      return (value: string) => value;
  }
};

export const getFieldValidator = (fieldType: string) => {
  switch (fieldType) {
    case 'phone':
    case 'tel':
      return validators.phone;
    case 'email':
      return validators.email;
    case 'firstname':
    case 'lastname':
      return validators.name;
    case 'companyName':
      return validators.companyName;
    case 'postalCode':
      return validators.postalCode;
    default:
      return () => true;
  }
};

export const getFieldErrorMessage = (fieldType: string): string => {
  switch (fieldType) {
    case 'phone':
    case 'tel':
      return 'Veuillez entrer un numéro de téléphone valide (format français ou international)';
    case 'email':
      return 'Veuillez entrer une adresse email valide';
    case 'firstname':
    case 'lastname':
      return 'Veuillez entrer un nom valide (lettres, espaces, tirets et apostrophes autorisés)';
    case 'companyName':
      return 'Veuillez entrer un nom de société valide';
    case 'postalCode':
      return 'Veuillez entrer un code postal valide (5 chiffres)';
    default:
      return 'Ce champ est requis';
  }
};
